// ============================================================
// newsletterGate.js
// Path: src/utils/newsletterGate.js
//
// Soft-gate helper for Knowledge Hub downloads.
//
// Flow:
//   1. User clicks "Download" on a Knowledge Hub report.
//   2. openGatedPdf() checks localStorage for prior submission.
//      - If found → open the PDF directly.
//      - If not   → redirect to /organizations with the PDF URL
//                   in the query string + #newsletter-signup hash.
//   3. After the user submits the newsletter form, NewsletterSignup
//      calls markNewsletterSubmitted() and renders a "Download now"
//      button that opens the gated URL.
//
// Notes:
//   - localStorage is per-browser. Cross-device dedup would require
//     a Mailchimp membership-lookup endpoint — flagged as a follow-up.
//   - All localStorage access is try/catch wrapped: handles SSR,
//     privacy mode, and storage quota errors gracefully.
//   - The /organizations route is hardcoded here. If the route ever
//     changes (e.g. to /partners), update GATED_REDIRECT_PATH.
// ============================================================

const STORAGE_KEY_EMAIL = 'regenera_newsletter_email';
const STORAGE_KEY_SUBMITTED_AT = 'regenera_newsletter_submitted_at';
const GATED_REDIRECT_PATH = '/organizations';
const NEWSLETTER_SECTION_ID = 'newsletter-signup';

const safeGetItem = key => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return null;
        return window.localStorage.getItem(key);
    } catch (e) {
        return null;
    }
};

const safeSetItem = (key, value) => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return;
        window.localStorage.setItem(key, value);
    } catch (e) {
        // Storage unavailable (privacy mode, quota, etc.) — fail silently.
    }
};

/**
 * @returns {boolean} true if this browser has previously submitted the
 *   newsletter form. Used to bypass the gate for returning users.
 */
export const hasSubmittedNewsletter = () => {
    return Boolean(safeGetItem(STORAGE_KEY_EMAIL));
};

/**
 * @returns {string|null} the email used during the previous submission,
 *   or null if no prior submission is recorded.
 */
export const getSubmittedEmail = () => safeGetItem(STORAGE_KEY_EMAIL);

/**
 * Marks the current browser as having submitted the newsletter form.
 * Call this only after a confirmed successful API response.
 *
 * @param {string} email - the email that was successfully submitted
 */
export const markNewsletterSubmitted = email => {
    if (email) safeSetItem(STORAGE_KEY_EMAIL, email);
    safeSetItem(STORAGE_KEY_SUBMITTED_AT, new Date().toISOString());
};

/**
 * Opens a PDF, gating behind the newsletter form if necessary.
 *
 * @param {string} pdfUrl - the S3 (or other public) URL of the report.
 * @param {object} opts
 * @param {string} [opts.reportTitle] - report title, passed to the
 *   signup page so it can show contextual copy ("...to download X").
 * @param {object} opts.history - react-router history instance, used
 *   for SPA navigation when the gate applies.
 */
export const openGatedPdf = (pdfUrl, { reportTitle, history } = {}) => {
    if (!pdfUrl) return;

    // Returning user — straight through.
    if (hasSubmittedNewsletter()) {
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
        return;
    }

    // First-time user — route them through the gate.
    if (!history) {
        // Fallback: if history isn't available for some reason, hard
        // navigate. Loses SPA niceness but the gate still works.
        const params = new URLSearchParams({ gated_url: pdfUrl });
        if (reportTitle) params.set('report_title', reportTitle);
        window.location.href = `${GATED_REDIRECT_PATH}?${params.toString()}#${NEWSLETTER_SECTION_ID}`;
        return;
    }

    const params = new URLSearchParams({ gated_url: pdfUrl });
    if (reportTitle) params.set('report_title', reportTitle);

    history.push({
        pathname: GATED_REDIRECT_PATH,
        search: `?${params.toString()}`,
        hash: `#${NEWSLETTER_SECTION_ID}`,
    });
};

/**
 * Read the gated context from the current URL query string, if any.
 * Used by NewsletterSignup to detect that the user arrived via a
 * gated download link and adapt its copy + success state.
 *
 * @returns {{ gatedUrl: string|null, reportTitle: string|null }}
 */
export const readGatedContext = () => {
    if (typeof window === 'undefined') return { gatedUrl: null, reportTitle: null };
    const params = new URLSearchParams(window.location.search);
    return {
        gatedUrl: params.get('gated_url'),
        reportTitle: params.get('report_title'),
    };
};
